/**
 * PRIMA GOLD - Luxury Jewelry Loupe Magnifier & PDP Zoom System
 * High-performance 60fps circular optical magnifying glass + instant click-to-zoom modal.
 */
(function () {
  'use strict';

  class PrimaJewelryLoupe {
    constructor() {
      this.lens = null;
      this.activeWrapper = null;
      this.activeImg = null;
      this.zoomFactor = 2.8;
      this.lensSize = 220; // 220px lens diameter
      this.isHovering = false;
      this.rafId = null;
      this.currentX = 0;
      this.currentY = 0;
      this.targetX = 0;
      this.targetY = 0;
      this.rect = null;

      this.init();
    }

    init() {
      this.createLens();
      this.bindEvents();

      // Re-bind when variants or DOM changes
      const observer = new MutationObserver(() => this.bindEvents());
      observer.observe(document.body, { childList: true, subtree: true });
    }

    createLens() {
      if (document.getElementById('PrimaJewelryLoupeLens')) {
        this.lens = document.getElementById('PrimaJewelryLoupeLens');
        return;
      }

      this.lens = document.createElement('div');
      this.lens.id = 'PrimaJewelryLoupeLens';
      this.lens.className = 'prima-jewelry-loupe-lens';
      this.lens.setAttribute('aria-hidden', 'true');
      document.body.appendChild(this.lens);
    }

    bindEvents() {
      const isTouch = window.matchMedia && window.matchMedia('(hover: none)').matches;

      // Find all main product showcase image elements
      const openers = document.querySelectorAll(
        '.product__modal-opener, .prima-pdp-main-viewer .product__media-item, .product-media-container'
      );

      openers.forEach((opener) => {
        if (opener.__loupeBound) return;
        opener.__loupeBound = true;

        if (!isTouch) {
          opener.addEventListener('mouseenter', (e) => this.onEnter(opener, e));
          opener.addEventListener('mousemove', (e) => this.onMove(opener, e));
          opener.addEventListener('mouseleave', () => this.onLeave());
        }

        // Direct Click-to-Zoom Handler for instant opening
        opener.addEventListener('click', (e) => {
          if (
            e.target.closest('.prima-pdp-brand-bar') ||
            e.target.closest('.deferred-media__poster') ||
            e.target.closest('.prima-pdp-showcase-arrow')
          ) return;
          
          const modalId = opener.getAttribute('data-modal') || '#ProductModal-' + (opener.closest('[data-section]')?.dataset.section || 'template--main');
          const modal = document.querySelector(modalId) || document.querySelector('product-modal');
          const button = opener.querySelector('button');

          if (modal && typeof modal.show === 'function') {
            e.preventDefault();
            this.hideLens();
            modal.show(button || opener);
          }
        });
      });
    }

    onEnter(opener, e) {
      const img = opener.querySelector('img:not(.prima-pdp-brand-logo-img)');
      if (!img) return;

      this.activeWrapper = opener;
      this.activeImg = img;
      this.rect = img.getBoundingClientRect();
      if (this.rect.width === 0) {
        this.rect = opener.getBoundingClientRect();
      }
      this.isHovering = true;

      // Get high-res image source
      const zoomSrc = img.getAttribute('data-zoom-src') || img.currentSrc || img.src;

      this.lens.style.backgroundImage = `url('${zoomSrc}')`;
      const bgW = this.rect.width * this.zoomFactor;
      const bgH = this.rect.height * this.zoomFactor;
      this.lens.style.backgroundSize = `${bgW}px ${bgH}px`;

      this.targetX = e.clientX;
      this.targetY = e.clientY;
      this.currentX = this.targetX;
      this.currentY = this.targetY;

      this.showLens();
      this.updatePosition();
    }

    onMove(opener, e) {
      if (!this.isHovering || !this.activeImg) {
        this.onEnter(opener, e);
        return;
      }

      // Hide if cursor is over the zoom trigger icon, brand bar, or showcase arrows
      if (
        (e.target.closest && e.target.closest('.product__media-icon')) ||
        (e.target.closest && e.target.closest('.prima-pdp-brand-bar')) ||
        (e.target.closest && e.target.closest('.prima-pdp-showcase-arrow'))
      ) {
        this.hideLens();
        return;
      }

      this.targetX = e.clientX;
      this.targetY = e.clientY;

      if (!this.lens.classList.contains('is-visible')) {
        this.showLens();
      }

      if (!this.rafId) {
        this.rafId = requestAnimationFrame(() => this.updatePosition());
      }
    }

    onLeave() {
      this.isHovering = false;
      this.hideLens();
      this.activeWrapper = null;
      this.activeImg = null;
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    showLens() {
      if (this.lens) {
        this.lens.classList.add('is-visible');
      }
    }

    hideLens() {
      if (this.lens) {
        this.lens.classList.remove('is-visible');
      }
    }

    updatePosition() {
      this.rafId = null;
      if (!this.isHovering || !this.activeImg || !this.lens) return;

      this.rect = this.activeImg.getBoundingClientRect();
      if (this.rect.width === 0 && this.activeWrapper) {
        this.rect = this.activeWrapper.getBoundingClientRect();
      }

      // Boundary check
      const pad = 20;
      if (
        this.targetX < this.rect.left - pad ||
        this.targetX > this.rect.right + pad ||
        this.targetY < this.rect.top - pad ||
        this.targetY > this.rect.bottom + pad
      ) {
        this.hideLens();
        return;
      }

      // Smooth lag-free tracking
      this.currentX += (this.targetX - this.currentX) * 0.55;
      this.currentY += (this.targetY - this.currentY) * 0.55;

      const halfLens = this.lensSize / 2;
      this.lens.style.left = `${this.currentX}px`;
      this.lens.style.top = `${this.currentY}px`;

      const relX = this.currentX - this.rect.left;
      const relY = this.currentY - this.rect.top;

      const bgW = this.rect.width * this.zoomFactor;
      const bgH = this.rect.height * this.zoomFactor;
      this.lens.style.backgroundSize = `${bgW}px ${bgH}px`;

      const bgX = relX * this.zoomFactor - halfLens;
      const bgY = relY * this.zoomFactor - halfLens;

      this.lens.style.backgroundPosition = `-${bgX}px -${bgY}px`;

      if (
        Math.abs(this.targetX - this.currentX) > 0.2 ||
        Math.abs(this.targetY - this.currentY) > 0.2
      ) {
        this.rafId = requestAnimationFrame(() => this.updatePosition());
      }
    }
  }

  // Self initialize immediately
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.__primaJewelryLoupe = new PrimaJewelryLoupe();
    });
  } else {
    window.__primaJewelryLoupe = new PrimaJewelryLoupe();
  }
})();
